import { useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Navigate } from 'react-router-dom';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { BASE_PERMISSION } from '@/lib/constants/modules';
import { usePermission } from '@/lib/permissions/use-permission';
import { useAuthStore } from '@/lib/auth/store';
import { usePermissionStore } from '@/lib/permissions/store';
import {
  useDeleteBlacklistOrDnd,
  useDeleteWhitelist,
  useSearchNumber,
} from '../api';

type ListToken = 'BLACKLIST' | 'DND' | 'WHITELIST';

function parseStatus(status: string): Set<ListToken> {
  const tokens = status
    .toUpperCase()
    .split(/[\s,/_-]+/)
    .filter(Boolean) as ListToken[];
  return new Set(tokens.filter((t) => t === 'BLACKLIST' || t === 'DND' || t === 'WHITELIST'));
}

export function SearchNumberPage() {
  const search = useSearchNumber();
  const deleteBlistOrDnd = useDeleteBlacklistOrDnd();
  const deleteWhitelist = useDeleteWhitelist();
  // Remove DND / Blacklist / Whitelist — ACL "Number Action" allow-list.
  // Key hi nahi hai (purana ACL) => sab allowed; [] => sab denied.
  const isSubSuperAdmin = useAuthStore((s) => s.isSubSuperAdmin());
  const numberActionAllowed = usePermissionStore((s) => s.moduleListing['Number Action']);
  const isActionAllowed = (key: string) => {
    if (!isSubSuperAdmin) return true;
    if (!Array.isArray(numberActionAllowed)) return true;
    return numberActionAllowed.includes(key);
  };
  const canRemoveBlacklist = isActionAllowed('remove_blacklist');
  const canRemoveDnd = isActionAllowed('remove_dnd');
  const canRemoveWhitelist = isActionAllowed('remove_whitelist');
  const canView = usePermission('Number Action.Search DND-Blist-Wlist', BASE_PERMISSION.VIEW);

  const [mobile, setMobile] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const result = search.data ?? null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = mobile.trim();
    if (!/^\d{10}$/.test(trimmed)) {
      toast.error('Enter a valid 10-digit mobile number.');
      return;
    }
    setSubmitted(true);
    search.mutate(trimmed, {
      onError: () => toast.error('Search failed.'),
    });
  };

  const handleRemoveBlist = () => {
    if (!result?.mobile) return;
    const m = result.mobile;
    deleteBlistOrDnd.mutate(
      { mobile: m, operation: 'Blacklist' },
      {
        onSuccess: (res) => {
          toast.success(res?.message ?? 'Removed from blacklist');
          search.mutate(m);
        },
        onError: () => toast.error('Could not remove from blacklist.'),
      },
    );
  };

  const handleRemoveDnd = () => {
    if (!result?.mobile) return;
    const m = result.mobile;
    deleteBlistOrDnd.mutate(
      { mobile: m, operation: 'DND' },
      {
        onSuccess: (res) => {
          toast.success(res?.message ?? 'Removed from DND');
          search.mutate(m);
        },
        onError: () => toast.error('Could not remove from DND.'),
      },
    );
  };

  const handleRemoveWhitelist = () => {
    if (!result?.mobile) return;
    const m = result.mobile;
    deleteWhitelist.mutate(m, {
      onSuccess: (res) => {
        toast.success(res?.message ?? 'Removed from whitelist');
        search.mutate(m);
      },
      onError: () => toast.error('Could not remove from whitelist.'),
    });
  };

  const tokens = result?.status ? parseStatus(result.status) : null;
  const busy =
    deleteBlistOrDnd.isPending || deleteWhitelist.isPending || search.isPending;

  if (!canView) return <Navigate to="/" replace />;

  return (
    <ScrollPage>
      <PageHeader
        title="Lookup Number"
        description="Check which of the three lists a mobile number is on, and remove it from any of them."
      />

      <Card className="sm:max-w-2xl">
        <CardHeader>
          <CardTitle>Lookup</CardTitle>
          <CardDescription>
            Enter the 10-digit mobile number you want to check.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="flex flex-wrap items-end gap-3">
            <div className="grid min-w-[220px] flex-1 gap-1.5">
              <Label htmlFor="mobile">
                Mobile<span className="ml-0.5 text-destructive">*</span>
              </Label>
              <Input
                id="mobile"
                inputMode="numeric"
                maxLength={10}
                value={mobile}
                onChange={(e) => {
                  setMobile(e.target.value.replace(/\D/g, '').slice(0, 10));
                  setSubmitted(false);
                }}
                placeholder="10-digit mobile number"
                autoFocus
              />
            </div>
            <Button type="submit" disabled={search.isPending}>
              {search.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
              Search
            </Button>
          </form>
        </CardContent>
      </Card>

      {submitted && !search.isPending && (
        <Card className="mt-4 sm:max-w-2xl">
          <CardHeader>
            <CardTitle>Result</CardTitle>
          </CardHeader>
          <CardContent>
            {!result || !result.mobile || !tokens || tokens.size === 0 ? (
              <p className="text-sm text-muted-foreground">
                {result?.message ?? (
                  <>
                    Mobile{' '}
                    <span className="font-mono font-medium text-foreground">
                      {mobile}
                    </span>{' '}
                    is not on any list.
                  </>
                )}
              </p>
            ) : (
              <div className="grid gap-4">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="text-muted-foreground">Mobile:</span>
                  <span className="font-mono font-medium text-foreground">
                    {result.mobile}
                  </span>
                  <span className="text-muted-foreground">·</span>
                  {Array.from(tokens).map((t) => (
                    <Badge key={t} variant={t === 'WHITELIST' ? 'outline' : 'secondary'}>
                      {t}
                    </Badge>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2">
                  {tokens.has('BLACKLIST') && canRemoveBlacklist && (
                    <Button
                      variant="outline"
                      onClick={handleRemoveBlist}
                      disabled={busy}
                    >
                      Remove from Blacklist
                    </Button>
                  )}
                  {tokens.has('DND') && canRemoveDnd && (
                    <Button
                      variant="outline"
                      onClick={handleRemoveDnd}
                      disabled={busy}
                    >
                      Remove from DND
                    </Button>
                  )}
                  {tokens.has('WHITELIST') && canRemoveWhitelist && (
                    <Button
                      variant="outline"
                      onClick={handleRemoveWhitelist}
                      disabled={busy}
                    >
                      Remove from Whitelist
                    </Button>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </ScrollPage>
  );
}
